// Components
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Feature } from '.generated/templates/Feature.Items.model';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { RichTextField } from '@sitecore-jss/sitecore-jss-react';
import classNames from 'classnames';
import Button from 'components/helpers/Button/Button';
import Container from 'components/helpers/Container/Container';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import { ChangeEvent, FormEvent, useState } from 'react';

export type BlueCardMedicalPolicyRouterProps = Sitecore.Override.ComponentBase &
  Feature.Components.BlueCardMedicalPolicyRouter.Fields.BlueCardMedicalPolicyRouter;

// Valid Error messages
type BlueCardMedicalPolicyErrors = 'blankInput' | 'fepError' | 'notValid';
type ErrorMessages = Record<BlueCardMedicalPolicyErrors, RichTextField | undefined>;

// Takes in a input and returns either true or the error type
const validateInput = (input: string): BlueCardMedicalPolicyErrors | null => {
  if (input.trim() === '') return 'blankInput';

  if ((input.startsWith('R') || input.startsWith('r')) && input.length === 1) {
    return 'fepError';
  }

  // Should never be possible, but show not valid error
  if (input.length > 3) return 'notValid';

  return input.length === 3 ? null : 'notValid';
};

const BlueCardMedicalPolicyRouter = ({ fields }: BlueCardMedicalPolicyRouterProps): JSX.Element => {
  const error_messages: ErrorMessages = {
    blankInput: fields?.blankErrorMessage,
    fepError: fields?.fepErrorMessage,
    notValid: fields?.notValidMessage,
  };

  const [input, setInput] = useState('');
  // if the user has clicked on the field or not
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<BlueCardMedicalPolicyErrors | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setInput(value);
    const error = validateInput(value);
    if (touched) setError(error);
    // FEP ERROR should only be set onSubmit
    if (error === 'fepError') setError(null);
  };

  // Show error only if users unfocuses from field or clicks the submit button
  // FEP ERROR will only show once the user types in R and submits
  const handleBlur = () => {
    setTouched(true);
    const error = validateInput(input);
    if (error !== 'fepError') setError(error);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    setTouched(true);
    const error = validateInput(input);
    setError(error);

    if (error) return;

    window.open(fields?.formSubmissionUrl.value.href + input, '_blank');
  };

  // Fail out if we don't have any fields
  if (!fields) {
    return <></>;
  }

  return (
    <Container
      theme="theme-blue--light"
      dataComponent="authorable/general/bluecardmedicalpolicyrouter"
      dataOverlapClass="container-content-padding-x py-16"
    >
      <div className="border border-gray flex flex-col p-10 space-y-6 bg-white rounded-lg md:space-y-12 shadow-card">
        <RichTextA11yWrapper field={fields.formHeader} />
        <form id="policy-router-form" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label htmlFor="policy-router-input" className="flex text-sm">
              <RichTextA11yWrapper field={fields.inputBoxLabel} />
            </label>
            <div className="flex space-x-4">
              <input
                id="policy-router-input"
                onChange={handleChange}
                onBlur={handleBlur}
                value={input}
                type="text"
                required
                maxLength={3}
                minLength={1}
                placeholder={fields.inputBoxGhostText.value}
                tabIndex={0}
                aria-invalid={error != undefined}
                className={classNames(
                  'w-full lg:w-64 h-14 p-4 pr-10 rounded-1 border-2 focus:outline-none focus:ring-2 focus:ring-blue',
                  { '!border-error': error !== null }
                )}
              />
              <Button
                text={fields.formSubmitButtonText.value}
                type="submit"
                disabled={!((input === 'R' && error !== 'fepError') || input.length === 3)}
              />
            </div>
            {error && error_messages[error] && (
              <div id="policy-router-error" role="alert" className="h-6 pt-1 text-error">
                <RichText field={error_messages[error]} />
              </div>
            )}
          </div>
        </form>
        <RichTextA11yWrapper field={fields.formFooter} />
      </div>
    </Container>
  );
};

export default BlueCardMedicalPolicyRouter;
